import { GridFilterPanel, useGridApiContext } from "@mui/x-data-grid-pro";
import { useLocation, useNavigate } from "react-router";
import { toast } from "react-toastify";
import {
  isUserScopedSavedFilterType,
  SavedFilterGridState,
  SavedFilterType,
} from "shared";

import { useGetSelectedOrganisation } from "../../../hooks/useGetSelectedOrganisation";
import {
  useCreateSavedFilterMutation,
  useDeleteSavedFilterMutation,
  useFetchSavedFiltersQuery,
  useUpdateSavedFilterMutation,
} from "../../../services/api/savedFiltersApi/savedFilters";
import {
  exportFilterGridState,
  MUI_DATAGRID_MAJOR_VERSION,
  withExportColumns,
} from "../../../lib/datagrid";

import { SavedFilterPresetsPanel } from "./SavedFilterPresetsPanel";

// Allow tables to declare which saved filter type the panel manages via
// slotProps={ { filterPanel: { savedFilterType: ... } } }
// eslint-disable-next-line quotes
declare module "@mui/x-data-grid" {
  interface FilterPanelPropsOverrides {
    savedFilterType: SavedFilterType;
    extraGridState?: Record<string, unknown>;
    exportParams?: Record<string, unknown>;
  }
}

type GridFilterPanelProps = React.ComponentProps<typeof GridFilterPanel>;

type ApiSavedFilterPanelProps = GridFilterPanelProps & {
  savedFilterType: SavedFilterType;
  /** Table state outside the grid to save alongside the filters (e.g. the actions status tab) */
  extraGridState?: Record<string, unknown>;
  /** The export API params for the current grid state, so the saved filter can be emailed on a schedule */
  exportParams?: Record<string, unknown>;
};

function countFilters(gridState: SavedFilterGridState) {
  const filter = gridState?.filter as { filterModel?: { items?: unknown[] } } | undefined;

  return filter?.filterModel?.items?.length ?? 0;
}

// Filter panel with saved filters persisted via the saved-filters API (shared
// across the org). The active saved filter is driven by the ?savedFilterId=
// URL param, so the sidebar shortcuts and this panel go through the same
// apply path (useApplySavedFilterFromUrl) in the table root.
export function ApiSavedFilterPanel({ savedFilterType, extraGridState, exportParams, ...gridFilterPanelProps }: ApiSavedFilterPanelProps) {
  const apiRef = useGridApiContext();
  const currentOrg = useGetSelectedOrganisation();
  const navigate = useNavigate();
  const location = useLocation();

  const savedFilterId = new URLSearchParams(location.search).get(`savedFilterId`);

  // User-scoped types (reports) have no org context; org-scoped requests wait
  // for the selected org
  const orgScoped = !isUserScopedSavedFilterType(savedFilterType);
  const organisationId = orgScoped ? currentOrg?.id : undefined;

  const { data } = useFetchSavedFiltersQuery(
    { organisationId, type: savedFilterType },
    { skip: orgScoped && !currentOrg?.id },
  );
  const [createSavedFilter, { isLoading: isCreating }] = useCreateSavedFilterMutation();
  const [updateSavedFilter] = useUpdateSavedFilterMutation();
  const [deleteSavedFilter] = useDeleteSavedFilterMutation();

  const savedFilters = data?.savedFilters ?? [];

  async function handleCreatePreset(name: string) {
    try {
      const created = await createSavedFilter({
        organisationId,
        name,
        type: savedFilterType,
        gridState: exportFilterGridState(apiRef, extraGridState),
        gridVersion: MUI_DATAGRID_MAJOR_VERSION,
        exportParams: withExportColumns(apiRef, exportParams),
      }).unwrap();

      navigate(`${location.pathname}?savedFilterId=${created.id}`);
    }
    catch (err) {
      toast.error(`Could not save filter. A saved filter with that name may already exist.`);
    }
  }

  async function handleUpdatePreset(id: string) {
    try {
      await updateSavedFilter({
        organisationId,
        id,
        gridState: exportFilterGridState(apiRef, extraGridState),
        gridVersion: MUI_DATAGRID_MAJOR_VERSION,
        exportParams: withExportColumns(apiRef, exportParams),
      }).unwrap();

      toast.success(`Saved filter updated`);
    }
    catch (err) {
      toast.error(`Could not update saved filter`);
    }
  }

  function handleLoadPreset(id: string) {
    navigate(`${location.pathname}?savedFilterId=${id}`);
  }

  async function handleDeletePreset(id: string) {
    try {
      await deleteSavedFilter({ organisationId, id }).unwrap();

      if (id === savedFilterId) {
        navigate(location.pathname);
      }
    }
    catch (err) {
      toast.error(`Could not delete saved filter`);
    }
  }

  return (
    <SavedFilterPresetsPanel
      gridFilterPanelProps={ gridFilterPanelProps }
      presets={ savedFilters.map(filter => ({
        id: filter.id,
        name: filter.name,
        filterCount: countFilters(filter.gridState),
      })) }
      activePresetId={ savedFilterId }
      creating={ isCreating }
      onCreatePreset={ handleCreatePreset }
      onUpdatePreset={ handleUpdatePreset }
      onLoadPreset={ handleLoadPreset }
      onDeletePreset={ handleDeletePreset }
    />
  );
}
